'use client'

import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { usePathname, useSearchParams } from 'next/navigation'

import { BuilderSection } from '@src/components/Builder/Builder'
import { CartPageContent } from '@src/components/CartPageContent/CartPageContent'
import { ModalBackdrop } from '@src/components/Modal/ModalBackdrop'
import { useModal } from '@src/components/Modal/ModalProvider'
import { useCart } from '@src/hooks/useCart'
import { useHeadlessUIReady } from '@src/hooks/useHeadlessUIReady'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getBuilderPreviewMode, processBuilderContent } from '@src/utils/builder.util'

export const CartDrawer = () => {
  const modal = useModal()
  const { cartDrawerBanner } = useCart()
  const searchParams = useSearchParams()
  const builderMode = getBuilderPreviewMode(searchParams)
  const { t, locale } = useLocale('cart-page')
  const pathname = usePathname()
  const isCartPage = pathname?.includes('/cart')
  const isHeadlessUIReady = useHeadlessUIReady()
  const isOpen = modal.isOpen('cart') && !isCartPage && isHeadlessUIReady

  return (
    <Dialog
      open={isOpen}
      onClose={() => modal.setClose('cart')}
      className="relative z-10 transition duration-1000 ease-out"
    >
      <ModalBackdrop className="hidden backdrop-blur-sm lg:block" />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-0 lg:pl-10">
            <DialogPanel
              transition
              className="pointer-events-auto flex w-screen max-w-md flex-col transition duration-200 ease-out data-closed:translate-x-full"
            >
              <div className="flex h-full flex-col bg-bodi-gray-pearl shadow-xl">
                <div className="sticky top-0 z-10 border-b border-bodi-gray-light bg-bodi-gray-pearl">
                  <BuilderSection
                    model="cart-drawer-banner"
                    content={processBuilderContent(cartDrawerBanner, builderMode)}
                    locale={locale.code}
                  />
                  <div className="flex items-start justify-between px-8 py-4">
                    <div className="flex h-7 w-full items-center justify-between">
                      <DialogTitle as="h3" className="text-heading-display-3 font-bold">
                        {t('your-cart')}
                      </DialogTitle>
                      <button
                        type="button"
                        onClick={() => modal.setClose('cart')}
                        className="relative text-gray-400 hover:text-gray-500"
                      >
                        <span className="absolute -inset-0.5" />
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon aria-hidden="true" className="size-6" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Main content with grow to push footer to bottom */}
                <div className="flex-1 overflow-y-auto">
                  <CartPageContent isDrawer={true} />
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
